import { create } from 'zustand'
import { dataset } from '@/data'
import { persistApi } from '@/api/client'
import type { Dataset } from '@/types/entities'

type CollKey = keyof Dataset
type Row<K extends CollKey> = Dataset[K][number]

// Collections whose in-app edits are PERSISTED to the API (crm.app_entities)
// — must match the server's WRITABLE_COLLECTIONS allowlist. Everything else
// (properties, funds, financialPeriods, …) is server-owned: local writes for
// those are cache updates only (e.g. lazy-loaded P&L).
const PERSISTED: ReadonlySet<CollKey> = new Set<CollKey>([
  'contacts', 'tracks', 'opportunities', 'actionItems', 'activities',
  'accounts', 'events', 'keyDates', 'projects', 'workOrders',
  // Key-info fields only — the API strips everything else (see entities.ts).
  'properties',
])

/** A write-through that failed — kept (with its exact request) so the user
 *  can see WHAT didn't save and retry it without redoing the edit. */
export interface FailedSave {
  key: string
  at: string // ISO
  coll: string
  /** Row id — lets a later successful write of the same record clear the entry. */
  id: string
  /** Human name of the record ("Canvass Capital", "James White"). */
  label: string
  /** What the write was ("changed: archived", "created", "deleted"). */
  detail: string
  error: string
  run: () => Promise<unknown>
}

/** Transient "Saved ✓" confirmation — rendered by SavedToast, replaced on
 *  each successful write-through (seq drives re-animation). */
export interface SavedPing {
  seq: number
  label: string
  detail: string
}

/** Best human name for a row across collections. */
function rowLabel(row: unknown, id: string): string {
  const r = (row ?? {}) as Record<string, unknown>
  const full = [r['firstName'], r['lastName']].filter(Boolean).join(' ')
  return String(r['name'] ?? (full || undefined) ?? r['title'] ?? r['subject'] ?? id)
}

let failedSaveSeq = 0
let savedPingSeq = 0

interface StoreState {
  data: Dataset
  /** wise-iam permission keys held by the signed-in user (UI gating only —
   *  the API re-checks every write server-side). */
  permissions: ReadonlySet<string>
  setPermissions: (perms: string[]) => void
  /** True once the API dataset has been loaded (see components/shell/DataGate). */
  hydrated: boolean
  /** When the dataset in `data` was last fetched from the API (epoch ms).
   *  Drives the "Updated Nm ago" chip — the app used to hydrate ONCE per page
   *  load and never again, so a record another user created was invisible
   *  until a full browser reload with nothing on screen to say so. */
  lastHydratedAt: number | null
  /** Write-throughs currently in flight. A background refresh REPLACES the
   *  whole dataset, so refreshing mid-write would visibly revert the row the
   *  user just edited until the server caught up — refreshes wait instead. */
  pendingWrites: number
  /** Replace the whole dataset — the API bootstrap path. */
  hydrate: (data: Dataset) => void
  /** Insert or replace a row by id. Persisted collections also write through. */
  upsert: <K extends CollKey>(coll: K, item: Row<K>) => void
  /** Shallow-merge a partial onto an existing row. Persisted collections also write through. */
  patch: <K extends CollKey>(coll: K, id: string, partial: Partial<Row<K>>) => void
  /** Remove a row by id (soft-deleted server-side for persisted collections). */
  remove: (coll: CollKey, id: string) => void
  /** Write-throughs that failed (deploy restart, network, …) — rendered by
   *  FailedSavesBanner. Each entry can re-send its original request. */
  failedSaves: FailedSave[]
  retryFailedSave: (key: string) => void
  dismissFailedSave: (key: string) => void
  /** Last successful write-through — rendered by SavedToast. */
  savedPing: SavedPing | null
}

export const useStore = create<StoreState>((set, get) => {
  // Fire-and-forget with a visible failure: the local update already happened
  // (optimistic), so a persistence miss must not fail silently — it lands in
  // failedSaves with the exact request so the user can see what and retry.
  const persist = (
    info: { coll: string; id: string; label: string; detail: string },
    run: () => Promise<unknown>,
  ): void => {
    set((s) => ({ pendingWrites: s.pendingWrites + 1 }))
    const settle = () => set((s) => ({ pendingWrites: Math.max(0, s.pendingWrites - 1) }))
    run().then(
      () => {
        settle()
        // Success clears any stale failure for the SAME record — otherwise a
        // failure from hours ago (deploy restart) keeps saying "didn't save"
        // while every subsequent save quietly works (Jason's 7/28 report).
        set((s) => ({
          failedSaves: s.failedSaves.filter((f) => !(f.coll === info.coll && f.id === info.id)),
          savedPing: { seq: ++savedPingSeq, label: info.label, detail: info.detail },
        }))
      },
      (err: unknown) => {
        settle()
        console.error(`[store] persist failed (${info.coll} — ${info.label}, ${info.detail}):`, err)
        const entry: FailedSave = {
          key: `fs-${++failedSaveSeq}`,
          at: new Date().toISOString(),
          error: err instanceof Error ? err.message : String(err),
          run,
          ...info,
        }
        set((s) => ({ failedSaves: [...s.failedSaves, entry] }))
      },
    )
  }

  return ({
  // Starts as the (now empty) bundled dataset; DataGate hydrates from the
  // API after sign-in. Kept as the initial value so the store shape stays
  // valid before hydration completes.
  data: dataset,
  permissions: new Set<string>(),
  setPermissions: (perms) => set({ permissions: new Set(perms) }),
  hydrated: false,
  lastHydratedAt: null,
  pendingWrites: 0,
  hydrate: (data) => set({ data, hydrated: true, lastHydratedAt: Date.now() }),
  upsert: (coll, item) => {
    const id = (item as { id: string }).id
    const existed = (get().data[coll] as { id: string }[]).some((x) => x.id === id)
    set((s) => {
      const arr = s.data[coll] as { id: string }[]
      const idx = arr.findIndex((x) => x.id === id)
      const next = idx >= 0 ? arr.map((x, i) => (i === idx ? item : x)) : [...arr, item]
      return { data: { ...s.data, [coll]: next } }
    })
    if (PERSISTED.has(coll)) {
      // Replacing an existing row goes through PATCH so a row that originated
      // from sync/seed becomes an override, not a duplicate.
      persist(
        { coll, id, label: rowLabel(item, id), detail: existed ? 'updated' : 'created' },
        () => existed
          ? persistApi.patch(coll, id, item as object)
          : persistApi.create(coll, item as { id: string }))
    }
  },
  patch: (coll, id, partial) => {
    set((s) => ({
      data: {
        ...s.data,
        [coll]: (s.data[coll] as { id: string }[]).map((x) =>
          x.id === id ? { ...x, ...partial } : x,
        ),
      },
    }))
    if (PERSISTED.has(coll)) {
      const row = (get().data[coll] as { id: string }[]).find((x) => x.id === id)
      persist(
        { coll, id, label: rowLabel(row, id), detail: `changed: ${Object.keys(partial as object).join(', ')}` },
        () => persistApi.patch(coll, id, partial as object))
    }
  },
  remove: (coll, id) => {
    const row = (get().data[coll] as { id: string }[]).find((x) => x.id === id)
    set((s) => ({
      data: { ...s.data, [coll]: (s.data[coll] as { id: string }[]).filter((x) => x.id !== id) },
    }))
    if (PERSISTED.has(coll)) {
      persist(
        { coll, id, label: rowLabel(row, id), detail: 'deleted' },
        () => persistApi.remove(coll, id))
    }
  },
  failedSaves: [],
  savedPing: null,
  retryFailedSave: (key) => {
    const f = get().failedSaves.find((x) => x.key === key)
    if (!f) return
    set((s) => ({ failedSaves: s.failedSaves.filter((x) => x.key !== key) }))
    // Re-enqueues on another failure (fresh timestamp/error).
    persist({ coll: f.coll, id: f.id, label: f.label, detail: f.detail }, f.run)
  },
  dismissFailedSave: (key) =>
    set((s) => ({ failedSaves: s.failedSaves.filter((x) => x.key !== key) })),
})})

/** Does the signed-in user hold a wise-iam permission? (UI gating only.) */
export const useHasPermission = (key: string): boolean =>
  useStore((s) => s.permissions.has(key))

/** Convenience hook: read one collection. */
export const useCollection = <K extends CollKey>(coll: K): Dataset[K] =>
  useStore((s) => s.data[coll])
